import { Lightning } from '@lightningjs/sdk';
import _merge from 'lodash/merge';
import { keyCodeToNumber } from '@pelo-plugins/keys';
import Icon from '../../../../icon/icon.component';
import { KeyboardItem } from '../../../keyItem/keyItem.component';
import { LETTER_SELECTED_SIGNAL, SPECIAL_KEYS } from '../../../keyboard.constants';
import { KEY_WIDTH, KEY_HEIGHT, KEYS_GAP, getBasicLetter, buttonColor } from '../../../keyItem/keyItem.style';
import { defaultContext, getBasicIconViewBox, getInitialStyle } from './keyboardNumbers.style';
import { DEFAULT_KEYBOARD, ITEMS_PER_ROW } from './keyboardNumbers.constants';

export default class KeyboardNumbers extends Lightning.Component {
  static _template() {
    return _merge(
      {
        visible: false,
        Background: {},
        Keys: {},
      },
      getInitialStyle(),
    );
  }

  _init() {
    this._index = 0;
    this._itemsPerRow = ITEMS_PER_ROW;
    this._style = defaultContext;
    this.keys = [];
    this.renderKeyboard();
  }

  get itemsPerRow() {
    return this._itemsPerRow;
  }

  set itemsPerRow(value) {
    this._itemsPerRow = value || ITEMS_PER_ROW;
  }

  renderKeyboard(value = null) {
    this._keys = value || DEFAULT_KEYBOARD;
    this._renderKeys(this._keys);
    this.visible = true;

    this._setIndex(0);
    this._refocus();
  }

  _renderKeys(characters) {
    const iconViewBox = getBasicIconViewBox(this._style.screenSize);

    this.patch({
      Keys: {
        children: characters.map((symbol, index) => {
          const icon = SPECIAL_KEYS[symbol] ? SPECIAL_KEYS[symbol].icon : null;

          const result = {
            type: KeyboardItem,
            x: (KEY_WIDTH[this._style.screenSize] + KEYS_GAP) * (index % this._itemsPerRow),
            y: (KEY_HEIGHT[this._style.screenSize] + KEYS_GAP) * Math.floor(index / this._itemsPerRow),
            ...(icon ? undefined : getBasicLetter({ symbol, ...this._style })),
          };

          if (icon) {
            result.Icon = {
              type: Icon,
              x: Math.round((KEY_WIDTH[this._style.screenSize] - iconViewBox.w) / 2),
              y: Math.round((KEY_HEIGHT[this._style.screenSize] - iconViewBox.h) / 2),
              color: buttonColor.innerElementUnFocused[this._style.colorMode],
              iconData: {
                picto: icon,
                viewBox: iconViewBox,
              },
            };

            result.Button = {
              color: buttonColor.iconBackgroundUnFocused[this._style.colorMode],
            };
          }

          return result;
        }),
      },
      w: (KEY_WIDTH[this._style.screenSize] + KEYS_GAP) * this._itemsPerRow,
      h: (KEY_HEIGHT[this._style.screenSize] + KEYS_GAP) * Math.ceil(characters.length / this._itemsPerRow),
    });
  }

  _handleUp() {
    if (this._index >= this._itemsPerRow) {
      this._setIndex(this._index - this._itemsPerRow);

      return true;
    }

    return false;
  }

  _handleDown() {
    const isNotLastRow = this._index + this._itemsPerRow < this._keys.length;
    if (isNotLastRow) {
      this._setIndex(this._index + this._itemsPerRow);
    }

    return false;
  }

  _handleLeft() {
    if (this._index > 0) {
      this._setIndex(this._index - 1);
    }

    return false;
  }

  _handleRight() {
    if (this._index < this._keys.length - 1) {
      this._setIndex(this._index + 1);
    }

    return false;
  }

  _handleEnter() {
    this.signal(LETTER_SELECTED_SIGNAL, this._keys[this._index]);

    return true;
  }

  _handleKey(event) {
    // eslint-disable-next-line prefer-const
    let { key, keyCode } = event;
    if (!key) {
      key = keyCodeToNumber(keyCode);
    }

    if (/^[0-9]$/.test(key)) {
      // eslint-disable-next-line eqeqeq
      const index = this._keys.findIndex(value => value == key);

      if (index > -1) {
        this._setIndex(index);

        return this._handleEnter();
      }
    }

    return false;
  }

  _setIndex(index = this._index) {
    this._index = index;
  }

  _focus() {
    this._setIndex();
  }

  _getFocused() {
    return this.tag('Keys').children[this._index];
  }
}
